/**
 * Status tematu (REALIZACJA / PÓŁKA / KOŃCZENIE / ZAMKNIĘTY) z edycją dla CEO / dyrektora
 * technicznego: klik w badge otwiera menu czterech statusów. Dla pozostałych ról
 * renderuje sam badge (view-only) — lustro backendowej bramki IsTechnicalDirector na
 * akcji topic-status/.
 *
 * Los umowy WYPIERA status tematu: po wgraniu wypowiedzenia widać „Umowa w trakcie
 * wypowiedzenia", po porozumieniu — „Umowa wypowiedziana (data)". Tego statusu nie da
 * się ustawić ręcznie (nie ma go w menu) — zmienia się wyłącznie przez dokumenty umowy
 * na karcie przedsięwzięcia, a to robi dyrektor techniczny; zwykły status wraca, gdy
 * umowa znów obowiązuje.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, ExternalLink, FileX2 } from "lucide-react";
import { clsx } from "clsx";
import { Badge } from "@/components/ui/Badge";
import { useSetTopicStatus } from "../hooks";
import type { TopicStatus } from "../types";
import { fmtDate } from "../utils";
import { type BadgeColor, TOPIC_STATUS, TOPIC_STATUS_ORDER, TopicStatusBadge } from "./ui";

export type ContractStatus = "active" | "terminating" | "terminated";

const CONTRACT_STATUS: Record<Exclude<ContractStatus, "active">, { color: BadgeColor; label: string }> = {
  terminating: { color: "orange", label: "Umowa w trakcie wypowiedzenia" },
  terminated: { color: "red", label: "Umowa wypowiedziana" },
};

const CONTRACT_TITLE =
  "Status wynika z losu umowy przedsięwzięcia — nie da się go ustawić ręcznie. " +
  "Zmienia go dyrektor techniczny przez dokumenty umowy (wypowiedzenie / porozumienie) " +
  "na karcie przedsięwzięcia; zwykły status tematu wraca, gdy umowa znów obowiązuje.";

/** Czy los umowy wypiera status tematu (wypowiedzenie w toku albo uskutecznione). */
export const contractOverridesStatus = (contract?: ContractStatus | null): contract is Exclude<ContractStatus, "active"> =>
  contract === "terminating" || contract === "terminated";

/** Kolor EFEKTYWNEGO statusu (temat albo los umowy) — do chipa w nagłówku workspace'u. */
export function effectiveStatusTone(topic: TopicStatus, contract?: ContractStatus | null): BadgeColor {
  if (contractOverridesStatus(contract)) return CONTRACT_STATUS[contract].color;
  return (TOPIC_STATUS[topic] ?? TOPIC_STATUS.in_progress).color;
}

function ContractDerivedStatus({
  status,
  terminatedOn,
  plain,
  projectId,
}: {
  status: Exclude<ContractStatus, "active">;
  terminatedOn?: string | null;
  plain: boolean;
  /** Karta przedsięwzięcia (dokumenty umowy) — tylko dla tych, którzy mogą tam coś zmienić. */
  projectId?: string;
}) {
  const s = CONTRACT_STATUS[status];
  const date = status === "terminated" && terminatedOn ? ` (${fmtDate(terminatedOn)})` : "";
  const link = projectId ? (
    <Link
      to={`/projects/${projectId}`}
      title="Dokumenty umowy na karcie przedsięwzięcia — tam zmienia się los umowy (dyrektor techniczny)"
      className="ml-1 inline-flex items-center opacity-70 hover:opacity-100"
      onClick={(e) => e.stopPropagation()}
    >
      <ExternalLink size={11} />
    </Link>
  ) : null;
  if (plain) {
    return (
      <span
        data-testid="contract-derived-status"
        title={CONTRACT_TITLE}
        className="inline-flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-wide"
      >
        <FileX2 size={12} />
        {s.label}
        {date && <span className="font-normal normal-case tracking-normal">{date}</span>}
        {link}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center" data-testid="contract-derived-status" title={CONTRACT_TITLE}>
      <Badge color={s.color} className="gap-1 whitespace-nowrap">
        <FileX2 size={12} />
        {s.label}
        {date && <span className="font-normal normal-case tracking-normal">{date}</span>}
      </Badge>
      {link}
    </span>
  );
}

export function TopicStatusControl({
  connectionId,
  status,
  canEdit,
  plain = false,
  contractStatus,
  contractTerminatedOn,
  projectId,
}: {
  connectionId: string;
  status: TopicStatus;
  canEdit: boolean;
  /** Sam tekst z kropką (bez ramki Badge) — gdy kolor niesie już chip nagłówka. */
  plain?: boolean;
  /** Los umowy przedsięwzięcia — gdy wypowiedziana, wypiera status tematu. */
  contractStatus?: ContractStatus | null;
  contractTerminatedOn?: string | null;
  /** Id przedsięwzięcia — link do karty z dokumentami umowy dla edytujących. */
  projectId?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const mutation = useSetTopicStatus(connectionId);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (contractOverridesStatus(contractStatus)) {
    return (
      <ContractDerivedStatus
        status={contractStatus}
        terminatedOn={contractTerminatedOn}
        plain={plain}
        projectId={canEdit ? projectId : undefined}
      />
    );
  }

  if (!canEdit) return <TopicStatusBadge status={status} plain={plain} />;

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={mutation.isPending}
        title="Zmień status tematu (DYR-TECH)"
        className="inline-flex items-center gap-0.5 disabled:opacity-60 hover:opacity-80 transition-opacity"
      >
        <TopicStatusBadge status={status} plain={plain} />
        <ChevronDown size={12} className={clsx(plain ? "ml-1 opacity-70" : "text-[var(--color-text-muted)]")} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 card shadow-hard-red min-w-[170px] py-1">
          {TOPIC_STATUS_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setOpen(false);
                if (s !== status) mutation.mutate(s);
              }}
              className="w-full flex items-center justify-between gap-3 px-3 py-1.5 text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              <TopicStatusBadge status={s} />
              {s === status && <Check size={13} className="text-[var(--color-primary)]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
